import { canonicalJson } from "@mfa/v5-contracts";

/**
 * The Idempotency-Key of one kind of call (runs.start, runs.replay), by decision D73: one key per
 * distinct request. The same input sent again (a retry after a lost answer, a double click)
 * carries the same key, so the server answers the first call's answer and starts nothing new.
 * Any change to the input (its project or draft revision, the mission, the budget, the runner,
 * the memory scopes, whatever else it names) mints a fresh key, so an edited request is a new
 * call and never 409 idempotency_key_reused. Once a call succeeds its key is forgotten, so the
 * next call, even with the same input, is a new one. Inputs compare by their canonical JSON
 * (contracts), so key order never matters.
 */
export class RetryKeys {
  private held: { input: string; key: string } | null = null;

  constructor(private readonly mint: () => string = () => crypto.randomUUID()) {}

  /** The key for this input: the held one while the input is unchanged, else a fresh one. */
  keyFor(input: unknown): string {
    const canonical = canonicalJson(input);
    if (this.held?.input !== canonical) this.held = { input: canonical, key: this.mint() };
    return this.held.key;
  }

  /** The call with this key succeeded: the next call gets a new key. */
  succeeded(key: string): void {
    if (this.held?.key === key) this.held = null;
  }
}
